import { Issue, IssueSortingField } from "@adsk/acc-issues-types";

// these keys are defined so our table logic is not reliant on the particular shape of the
// issue object we receive from the backend
export enum ISSUE_COLUMN_KEYS {
  STATUS = "STATUS",
  ID = "ID",
  TITLE = "TITLE",
  LOCATION_DETAILS = "LOCATION_DETAILS",
  CREATED_BY = "CREATED_BY",
  ASSIGNED_TO = "ASSIGNED_TO",
  WATCHERS = "WATCHERS",
  COMPANY = "COMPANY",
  START_DATE = "START_DATE",
  DUE_DATE = "DUE_DATE",
  DOCUMENT = "DOCUMENT",
  ATTACHMENTS = "ATTACHMENTS",
  COMMENTS = "COMMENTS",
  LBS_LOCATION = "LBS_LOCATION",
  ROOT_CAUSE = "ROOT_CAUSE",
  ISSUE_TYPE_CATEGORY = "ISSUE_TYPE_CATEGORY",
  ISSUE_TYPE = "ISSUE_TYPE",
  OPENED_AT = "OPENED_AT",
  CLOSED_AT = "CLOSED_AT",
  CREATED_AT = "CREATED_AT",
  UPDATED_AT = "UPDATED_AT",
  UPDATED_BY = "UPDATED_BY",
  DESCRIPTION = "DESCRIPTION",
  PLACEMENT = "PLACEMENT",
  IS_PUBLISHED = "IS_PUBLISHED",
  REFERENCES = "REFERENCES",
  DELETED_BY = "DELETED_BY",
  DELETED_ON = "DELETED_ON",
}

export const SELECT_COLUMN = "select";
export const ACTIONS_COLUMN = "actions";

export const CUSTOM_FIELD_COLUMN_PREFIX = "custom_";

export const isCustomFieldColumn = (id: string): boolean =>
  id.startsWith(CUSTOM_FIELD_COLUMN_PREFIX);

export type ColumnDataKey = keyof Issue | "select" | "references" | "placement";

// maps the keys we've defined to the issue's data shape
export const ISSUE_COLUMN_DATA_KEYS: {
  [key in ISSUE_COLUMN_KEYS]: ColumnDataKey;
} = Object.freeze({
  [ISSUE_COLUMN_KEYS.STATUS]: "status",
  [ISSUE_COLUMN_KEYS.ID]: "displayId",
  [ISSUE_COLUMN_KEYS.TITLE]: "title",
  [ISSUE_COLUMN_KEYS.LOCATION_DETAILS]: "locationDetails",
  [ISSUE_COLUMN_KEYS.CREATED_BY]: "createdBy",
  [ISSUE_COLUMN_KEYS.CREATED_AT]: "createdAt",
  [ISSUE_COLUMN_KEYS.ASSIGNED_TO]: "assignedTo",
  [ISSUE_COLUMN_KEYS.WATCHERS]: "watcherObjects",
  [ISSUE_COLUMN_KEYS.COMPANY]: "assignedTo",
  [ISSUE_COLUMN_KEYS.START_DATE]: "startDate",
  [ISSUE_COLUMN_KEYS.DUE_DATE]: "dueDate",
  [ISSUE_COLUMN_KEYS.DOCUMENT]: "placements",
  [ISSUE_COLUMN_KEYS.ATTACHMENTS]: "attachmentCount",
  [ISSUE_COLUMN_KEYS.COMMENTS]: "commentCount",
  [ISSUE_COLUMN_KEYS.LBS_LOCATION]: "locationId",
  [ISSUE_COLUMN_KEYS.ROOT_CAUSE]: "rootCauseId",
  [ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY]: "issueTypeCategoryId",
  [ISSUE_COLUMN_KEYS.ISSUE_TYPE]: "issueTypeId",
  [ISSUE_COLUMN_KEYS.OPENED_AT]: "openedAt",
  [ISSUE_COLUMN_KEYS.CLOSED_AT]: "closedAt",
  [ISSUE_COLUMN_KEYS.UPDATED_AT]: "updatedAt",
  [ISSUE_COLUMN_KEYS.UPDATED_BY]: "updatedBy",
  [ISSUE_COLUMN_KEYS.DESCRIPTION]: "description",
  [ISSUE_COLUMN_KEYS.PLACEMENT]: "placement",
  [ISSUE_COLUMN_KEYS.IS_PUBLISHED]: "published",
  [ISSUE_COLUMN_KEYS.REFERENCES]: "references",
  [ISSUE_COLUMN_KEYS.DELETED_BY]: "deletedBy",
  [ISSUE_COLUMN_KEYS.DELETED_ON]: "deletedAt",
});

export const ISSUE_COLUMN_SORT_ATTRS: {
  [key in ISSUE_COLUMN_KEYS]?: IssueSortingField;
} = {
  [ISSUE_COLUMN_KEYS.STATUS]: IssueSortingField.Status,
  [ISSUE_COLUMN_KEYS.ID]: IssueSortingField.DisplayId,
  [ISSUE_COLUMN_KEYS.TITLE]: IssueSortingField.Title,
  [ISSUE_COLUMN_KEYS.LOCATION_DETAILS]: IssueSortingField.LocationDetails,
  [ISSUE_COLUMN_KEYS.CREATED_AT]: IssueSortingField.CreatedAt,
  [ISSUE_COLUMN_KEYS.DUE_DATE]: IssueSortingField.DueDate,
  [ISSUE_COLUMN_KEYS.START_DATE]: IssueSortingField.StartDate,
  [ISSUE_COLUMN_KEYS.ROOT_CAUSE]: IssueSortingField.RootCause,
  [ISSUE_COLUMN_KEYS.OPENED_AT]: IssueSortingField.OpenedAt,
  [ISSUE_COLUMN_KEYS.CLOSED_AT]: IssueSortingField.ClosedAt,
  [ISSUE_COLUMN_KEYS.UPDATED_AT]: IssueSortingField.UpdatedAt,
  [ISSUE_COLUMN_KEYS.DESCRIPTION]: IssueSortingField.Description,
  [ISSUE_COLUMN_KEYS.DELETED_ON]: IssueSortingField.DeletedOn,
  [ISSUE_COLUMN_KEYS.DELETED_BY]: IssueSortingField.DeletedBy,
  [ISSUE_COLUMN_KEYS.ISSUE_TYPE]: IssueSortingField.IssueType,
  [ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY]: IssueSortingField.IssueTypeCategory,
  [ISSUE_COLUMN_KEYS.IS_PUBLISHED]: IssueSortingField.Published,
};

export const COLUMN_KEY_BY_SORT_ATTRS: {
  [key in IssueSortingField]?: ISSUE_COLUMN_KEYS;
} = {
  [IssueSortingField.Status]: ISSUE_COLUMN_KEYS.STATUS,
  [IssueSortingField.DisplayId]: ISSUE_COLUMN_KEYS.ID,
  [IssueSortingField.Title]: ISSUE_COLUMN_KEYS.TITLE,
  [IssueSortingField.LocationDetails]: ISSUE_COLUMN_KEYS.LOCATION_DETAILS,
  [IssueSortingField.CreatedAt]: ISSUE_COLUMN_KEYS.CREATED_AT,
  [IssueSortingField.DueDate]: ISSUE_COLUMN_KEYS.DUE_DATE,
  [IssueSortingField.StartDate]: ISSUE_COLUMN_KEYS.START_DATE,
  [IssueSortingField.RootCause]: ISSUE_COLUMN_KEYS.ROOT_CAUSE,
  [IssueSortingField.OpenedAt]: ISSUE_COLUMN_KEYS.OPENED_AT,
  [IssueSortingField.ClosedAt]: ISSUE_COLUMN_KEYS.CLOSED_AT,
  [IssueSortingField.UpdatedAt]: ISSUE_COLUMN_KEYS.UPDATED_AT,
  [IssueSortingField.Description]: ISSUE_COLUMN_KEYS.DESCRIPTION,
  [IssueSortingField.DeletedOn]: ISSUE_COLUMN_KEYS.DELETED_ON,
  [IssueSortingField.DeletedBy]: ISSUE_COLUMN_KEYS.DELETED_BY,
  [IssueSortingField.IssueType]: ISSUE_COLUMN_KEYS.ISSUE_TYPE,
  [IssueSortingField.IssueTypeCategory]: ISSUE_COLUMN_KEYS.ISSUE_TYPE_CATEGORY,
  [IssueSortingField.Published]: ISSUE_COLUMN_KEYS.IS_PUBLISHED,
};
